import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";

export type Transport = "hls" | "webrtc";
export type UserOverride = "inherit" | Transport;

export interface TransportSettings {
  tenant_default: Transport;
  user_override: UserOverride;
  effective: Transport;
}

export interface IceServer {
  urls: string[];
  username?: string | null;
  credential?: string | null;
}

export interface LiveViewConfig {
  effective_transport: Transport;
  ice_servers: IceServer[];
  turn_configured: boolean;
}

export interface LiveViewResponse {
  transport: Transport;
  url: string | null;
  expires_at?: unknown;
  camera_id: string;
  has_source: boolean;
}

const TRANSPORT_KEY = ["live-view", "transport"] as const;
const CONFIG_KEY = ["live-view", "config"] as const;

export function useTransportSettings() {
  return useQuery({
    queryKey: TRANSPORT_KEY,
    queryFn: async () => {
      const { data } = await api.get<TransportSettings>(
        "/api/v1/settings/live-view-transport",
      );
      return data;
    },
  });
}

export function useUpdateTenantTransport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (transport: Transport) => {
      const { data } = await api.patch<TransportSettings>(
        "/api/v1/settings/live-view-transport/tenant",
        { transport },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TRANSPORT_KEY }),
  });
}

export function useUpdateUserTransport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (transport: UserOverride) => {
      const { data } = await api.patch<TransportSettings>(
        "/api/v1/settings/live-view-transport/user",
        { transport },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TRANSPORT_KEY }),
  });
}

export function useLiveViewConfig() {
  return useQuery({
    queryKey: CONFIG_KEY,
    queryFn: async () => {
      const { data } = await api.get<LiveViewConfig>(
        "/api/v1/settings/live-view-config",
      );
      return data;
    },
    staleTime: 60_000,
  });
}

export function useCameraLiveView(cameraId: string | null) {
  return useQuery({
    queryKey: ["live-view", "camera", cameraId],
    enabled: !!cameraId,
    queryFn: async () => {
      const { data } = await api.get<LiveViewResponse>(
        `/api/v1/cameras/${cameraId}/live-view`,
      );
      return data;
    },
    // Refresh roughly halfway through the 15-minute token TTL so
    // the player always has a fresh URL when reconnecting.
    refetchInterval: 7 * 60 * 1000,
  });
}


// S-XCODE / U1c-7 — ▶ warm-up for agent-relay cameras. The grid tile POSTs
// this on play; the backend starts the H.265 relay (if not already live) and
// reports source readiness so the tile can poll until it's safe to load the
// H264 transcode URL. Idempotent: repeated polls don't re-dispatch the relay.
// ``check_failed`` (2026-08-04) is NOT a warm-up phase and must never be
// polled through like one. It means the backend could not obtain a readiness
// answer at all (MediaMTX control API unreachable / timed out / non-200).
// It used to be reported as ``starting``, which told the operator to wait —
// the opposite of the truth, since the stream may already be publishing. A
// tile that cannot distinguish "starting" from "we cannot tell" reproduces
// the backend's own defect one layer up, which is why it is its own state
// here rather than a flag on ``starting``.
export type EnsureLiveViewState =
  | "ready"
  | "starting"
  | "unavailable"
  | "busy"
  | "degraded"
  | "check_failed";

export interface EnsureLiveViewResponse {
  state: EnsureLiveViewState;
  source_ready: boolean;
  relay_requested: boolean;
  detail?: string | null;
  // P1B-FE (optional; present only on the P1B resolver path).
  failure_reason_code?: string | null;
  tile_state?: string | null; // DEGRADED | CAPACITY | FAILOVER | null
}

export async function ensureLiveView(
  cameraId: string,
): Promise<EnsureLiveViewResponse> {
  const { data } = await api.post<EnsureLiveViewResponse>(
    `/api/v1/cameras/${cameraId}/live-view/ensure`,
  );
  return data;
}
